import { createFileRoute } from "@tanstack/react-router";
import { SeoPage } from "@/components/SeoPage";
import { faqJsonLd } from "@/lib/seo-jsonld";
import { breadcrumbJsonLd } from "@/lib/seo-jsonld";
import { KOMMUN_LINKS, SOURCE_FOHM_VAGLEDNING, SOURCE_ALKOHOLLAGEN, LAST_REVIEWED } from "@/lib/official-sources";

const faqs = [
  { q: "Vart ansöker jag om serveringstillstånd i Ronneby?", a: "Ansökan görs till tillståndsenheten i Ronneby kommun. Verksamhet i annan kommun i Blekinge (t.ex. Karlskrona, Karlshamn, Sölvesborg, Olofström) söker hos respektive kommun." },
  { q: "Vad kostar serveringstillstånd i Ronneby?", a: "Ronneby kommun tar ut en ansökningsavgift och en årlig tillsynsavgift beroende på tillståndstyp och alkoholomsättning – aktuella avgifter finns på kommunens webbplats." },
  { q: "Hur lång är handläggningstiden i Ronneby?", a: "Räkna med 8–12 veckor för en komplett ansökan om stadigvarande tillstånd." },
];

export const Route = createFileRoute("/serveringstillstand-ronneby")({
  component: () => (
    <SeoPage
      breadcrumbs={[
        { label: "Hem", to: "/" },
        { label: "Guider", to: "/serveringstillstand-och-kunskapsprov" },
        { label: "Ronneby" },
      ]}
      title="Serveringstillstånd Ronneby – ansökan och krav"
      intro="Allt du behöver veta om serveringstillstånd i Ronneby: var du ansöker, vilka krav som ställs och hur du förbereder dig för kunskapsprovet."
      lastReviewed={LAST_REVIEWED}
      sources={[KOMMUN_LINKS.ronneby, SOURCE_FOHM_VAGLEDNING, SOURCE_ALKOHOLLAGEN]}
      sections={[
        { h2: "Var ansöker du i Ronneby?", body: "Tillståndsenheten i Ronneby kommun ansvarar för serveringstillstånd. För verksamhet i grannkommuner som Karlskrona, Karlshamn eller Olofström ansöker du hos respektive kommun." },
        { h2: "Krav från Ronneby kommun", body: "Utöver de nationella kraven (personlig och ekonomisk lämplighet, godkänt kunskapsprov och lämplig lokal) har Ronneby en tydlig besöks- och konferensnäring runt Brunnsparken och skärgården. Från 1 juni 2026 är kraven på eget kök och varierat matutbud borttagna nationellt." },
        { h2: "Kunskapsprovet i Ronneby", body: "Provet genomförs hos kommunen efter att ansökan har kommit in. Skriftligt prov inom alkohollagen, serveringsbestämmelser, ordning, nykterhet och marknadsföring enligt FoHMFS 2014:7." },
        { h2: "Handläggningstid", body: "Räkna med 8–12 veckor från komplett ansökan till beslut. Sök i god tid inför sommarsäsong och evenemang." },
        { h2: "Tillsyn och uppföljning", body: "Ronneby kommun bedriver tillsyn tillsammans med polis och Skatteverket. Anmärkningar kan leda till erinran, varning, sanktionsavgift eller återkallat tillstånd." },
        { h2: "Lokala särdrag – Brunnsparken och Blekinges natur", body: "Ronneby präglas av sin gamla kurortstradition i Brunnsparken samt kust- och skärgårdsläge. Konferensverksamhet, spa och restauranger står för en stor del av serveringstillstånden – anpassa ansökan efter typ av gäst och verksamhet." },
      ]}
      faqs={faqs}
      related={{
        title: "Relaterade sidor",
        links: [
          { to: "/serveringstillstand-och-kunskapsprov", label: "Hub – alla guider" },
          { to: "/serveringstillstand-karlshamn", label: "Serveringstillstånd Karlshamn" },
          { to: "/serveringstillstand-karlskrona", label: "Serveringstillstånd Karlskrona" },
          { to: "/serveringstillstand-restaurang", label: "För restaurang" },
          { to: "/serveringstillstand-catering", label: "För catering" },
          { to: "/tillfalligt-serveringstillstand", label: "Tillfälligt tillstånd" },
          { to: "/kunskapsprov-alkohollagen", label: "Kunskapsprov i alkohollagen" },
          { to: "/foretag", label: "Företagslösning" },
        ],
      }}
    />
  ),
  head: () => ({
    meta: [
      { title: "Serveringstillstånd Ronneby – krav, prov & tips" },
      { name: "description", content: "Så får du serveringstillstånd i Ronneby: lokala krav, tillsynsavgifter och hur du klarar kunskapsprovet." },
      { property: "og:title", content: "Serveringstillstånd Ronneby – tips" },
      { property: "og:description", content: "Ansökan, krav, avgifter och kunskapsprov för serveringstillstånd i Ronneby." },
      { property: "og:url", content: "https://alkoholprovet.se/serveringstillstand-ronneby" },
    ],
    links: [{ rel: "canonical", href: "https://alkoholprovet.se/serveringstillstand-ronneby" }],
    scripts: [
      faqJsonLd(faqs),
      breadcrumbJsonLd([
        { label: "Hem", url: "https://alkoholprovet.se/" },
        { label: "Guider", url: "https://alkoholprovet.se/serveringstillstand-och-kunskapsprov" },
        { label: "Ronneby", url: "https://alkoholprovet.se/serveringstillstand-ronneby" },
      ]),
    ],
  }),
});
